import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { useConfigStore } from '@renderer/stores/configState'
import PhotoFrame from '@renderer/components/PhotoFrame'
import { useTranslation } from 'react-i18next'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { useCachedImage } from '@renderer/hooks/useCachedImage'

const RetakePage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, gallery, userPreferences, uploadSessionState } = useSessionStateStore()
  const { config: globalConfig } = useConfigStore()
  const config = globalConfig?.pages.retake

  const isPotrait =
    (globalConfig?.screenSize.width || 1080) < (globalConfig?.screenSize.height || 1920)
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([])
  const [remainingTime, setRemainingTime] = useState<string>('--')

  const maxPhotos = config?.settings?.maxRetakeCount
  const retakeTimeout = config?.settings?.retakeTimeout || 60
  const maxRetakeSession = config?.settings?.maxRetakeSession

  // Initialize countdown hook
  const retakeCountdown = useCountdown(retakeTimeout)

  // Count how many retake sessions have occurred
  const retakeSessionCount = userPreferences.retakeSessionsCompleted || 0

  // Check if retakes are still allowed
  const canRetake =
    maxRetakeSession === undefined ||
    maxRetakeSession === null ||
    retakeSessionCount < maxRetakeSession

  useEffect((): void | (() => void) => {
    if (!canRetake) {
      navigate(`/${projectId}/select`)
      return
    }

    // Upload session state on mount
    uploadSessionState()

    if (retakeTimeout > 0) {
      // Set up countdown callbacks
      retakeCountdown.setOnTick(() => {
        setRemainingTime(retakeCountdown.getFormattedRemainingSeconds())
      })

      retakeCountdown.setOnComplete(() => {
        // Log countdown timeout
        setSessionState({
          timeline: {
            activityLogs: [
              {
                page: 'retake',
                type: 'selection',
                action: 'timeout',
                details: {
                  selectedCount: selectedPhotos.length,
                  timeoutDuration: retakeTimeout
                },
                timestamp: new Date()
              }
            ]
          }
        })
        handleContinue()
      })

      retakeCountdown.start()

      return (): void => {
        retakeCountdown.stop()
      }
    }
  }, [])

  const handlePhotoClick = (id: string): void => {
    setSelectedPhotos((prev) => {
      if (prev.includes(id)) {
        return prev.filter((i) => i !== id)
      }
      // If maxPhotos is null/undefined, allow unlimited selections
      if (maxPhotos === null || maxPhotos === undefined || prev.length < maxPhotos) {
        return [...prev, id]
      }
      return prev
    })
  }

  const handleRetake = async (): Promise<void> => {
    // Log processing start
    setSessionState({
      timeline: {
        activityLogs: [
          {
            page: 'retake',
            type: 'processing',
            action: 'start',
            details: {
              selectedCount: selectedPhotos.length,
              selectedPhotoIds: selectedPhotos
            },
            timestamp: new Date()
          }
        ]
      }
    })

    try {
      // Filter out selected items and their URLs
      const remainingItems = gallery.capturedItems.filter(
        (item) => !selectedPhotos.includes(item.id)
      )

      // Clean up URLs and delete selected photos
      const itemsToDelete = gallery.capturedItems.filter((item) => selectedPhotos.includes(item.id))
      itemsToDelete.forEach((item) => {
        if (item.photoUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(item.photoUrl)
        }
        if (item.videoUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(item.videoUrl)
        }
        if (item.recordingUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(item.recordingUrl)
        }
      })

      // Update session state
      setSessionState({
        userPreferences: {
          retakeCount: selectedPhotos.length,
          retakeSessionsCompleted: userPreferences.retakeSessionsCompleted + 1
        },
        gallery: {
          capturedItems: remainingItems
          // Stats tracked by Gallery API
        }
      })

      // Upload session state before navigating
      uploadSessionState()
      navigate(`/${projectId}/capture`)
    } catch (error) {
      console.error('Error in retake process:', error)

      // Log processing failure
      setSessionState({
        timeline: {
          activityLogs: [
            {
              page: 'retake',
              type: 'processing',
              action: 'failure',
              details: {
                error: error instanceof Error ? error.message : 'Unknown error',
                selectedCount: selectedPhotos.length
              },
              timestamp: new Date()
            }
          ]
        }
      })
    }
  }

  const handleContinue = (): void => {
    navigate(`/${projectId}/select`)
  }

  // Call all hooks at the top level
  const cachedRetakeBg = useCachedBackgroundImage('/assets/photophoria/photophoria-retake.webp')
  const cachedRetakeRetake = useCachedImage('/assets/photophoria/photophoria-retake-retake.webp')
  const cachedRetakeKeep = useCachedImage('/assets/photophoria/photophoria-retake-keep.webp')

  return (
    <div
      className="relative w-full h-full bg-cover bg-center flex flex-col"
      data-page="retake"
      style={{
        backgroundImage: `url(${cachedRetakeBg})`
      }}
    >
      {/* Header - Content removed as per user request (background handles title) */}
      {/* {maxRetakeSession && (
        <p className="text-lg text-plaster/60 mt-2">
          {t('retake.sessionCount', 'Retake session {{current}} of {{max}}', {
            current: retakeSessionCount + 1,
            max: maxRetakeSession
          })}
        </p>
      )} */}

      {/* Photo Grid */}
      <div className={`flex-1 overflow-y-auto h-full my-8 mt-80 mx-64`} role="listbox">
        <div
          className={` 
          grid gap-6
          ${
            gallery.capturedItems.filter((item) => item.photoUrl).length <= 3
              ? `grid-cols-${gallery.capturedItems.filter((item) => item.photoUrl).length} max-w-full`
              : `grid-cols-4 max-w-full`
          }
        `}
        >
          {gallery.capturedItems
            .filter((item) => item.photoUrl)
            .map((item, index) => {
              const isSelected = selectedPhotos.includes(item.id)

              return (
                <button
                  role="option"
                  type="button"
                  key={item.id}
                  onClick={() => handlePhotoClick(item.id)}
                  className={`
                  group relative overflow-hidden rounded-xl transition-all duration-300
                  ${isSelected ? 'ring-4 ring-red-500 scale-95 grayscale' : 'hover:scale-[0.98]'}
                `}
                  style={{
                    boxShadow: isSelected ? `0 0 0 4px #EF4444` : ''
                  }}
                >
                  <span className="sr-only">
                    {t('retake.photoAlt', 'Retake Photo {{number}}', { number: index + 1 })}
                  </span>
                  {item.photoUrl && (
                    <>
                      <PhotoFrame
                        src={item.photoUrl}
                        alt={t('retake.photoAlt', 'Photo {{number}}', { number: index + 1 })}
                        className="w-full aspect-[3/4] object-cover"
                        displayRatio={userPreferences.framePhotoRatio || 1}
                      />
                      <div
                        className={`
                      absolute inset-0 flex items-center justify-center
                      bg-black/50 transition-opacity duration-300
                      ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
                    `}
                      >
                        <div className="text-center text-white">
                          <div className="text-4xl mb-2">{isSelected ? '❌' : '🔄'}</div>
                          <p className="text-lg font-medium">
                            {isSelected
                              ? t('retake.selectedToRetake', 'Selected to Retake')
                              : t('retake.clickToRetake', 'Click to Retake')}
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </button>
              )
            })}
        </div>
      </div>

      {/* Footer Actions */}
      <div
        className={`flex justify-between items-center mb-4 ${isPotrait ? 'px-12 py-16' : 'px-56 pb-20'}`}
      >
        {/* Retake Button */}
        <button
          onClick={handleRetake}
          disabled={selectedPhotos.length === 0}
          className={`transition-transform transform hover:scale-105 ${selectedPhotos.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <img
            src={cachedRetakeRetake}
            alt={t('retake.retakeSelected', 'Retake Selected Photos')}
            className="w-[350px] h-auto"
          />
        </button>

        <div className="flex items-center gap-6">
          <span className="text-2xl text-black/80">{remainingTime}</span>
          {/* Keep All Photos Button */}
          <button
            onClick={handleContinue}
            disabled={selectedPhotos.length > 0}
            className={`transition-transform transform hover:scale-105 ${selectedPhotos.length > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <img
              src={cachedRetakeKeep}
              alt={t('retake.keepAll', 'Keep All Photos')}
              className="w-[350px] h-auto"
            />
          </button>
        </div>
      </div>
    </div>
  )
}

export default RetakePage
